import React, { FC, createContext, useState, useEffect } from "react";
import { ApolloClient } from "apollo-client";
import * as Sentry from "@sentry/browser";
import {
  getCheckout_checkout,
  getCheckout_checkout_lineItems,
} from "../web-nextjs/__generated__/getCheckout";
import { useCheckout } from "./hooks";
import useLineItems from "./useLineItems";
import useOrder, { MakeOrder } from "./useOrder";
import { getCheckoutInfoWithPaymentId, updateCheckout } from "./actions";
import { updateCheckout_updateCheckout } from "../web-nextjs/__generated__/updateCheckout";
import { CheckoutUpdateInput } from "../web-nextjs/__generated__/globalTypes";

interface ActionProps {
  id: string;
  ingredients?: {
    id: string;
    amount: number;
  }[];
}

interface OptimisticLineItems extends getCheckout_checkout_lineItems {
  expectedQuantity: number;
  expectedPrice: number;
}

interface CheckoutContextInterface {
  checkout: getCheckout_checkout;
  checkoutRef: React.MutableRefObject<getCheckout_checkout>;
  addToCart: (ActionProps) => void;
  removeFromCart: (ActionProps) => void;
  optimisticLineItems: OptimisticLineItems[];
  ingredientIds: string[];
  makeOrder: MakeOrder;
  updateCheckoutAndSave: (
    data: CheckoutUpdateInput
  ) => Promise<updateCheckout_updateCheckout>;
  getCheckoutWithPayment: (paymentId: string) => Promise<getCheckout_checkout>;
  promocodeError: string;
  bonuses: number;
  setBonuses: React.Dispatch<React.SetStateAction<number>>;
}

const CheckoutContext = createContext<CheckoutContextInterface>(null);
export default CheckoutContext;

interface Props {
  client: ApolloClient<any>;
  initCheckout: any;
}

export const CheckoutProvider: FC<Props> = ({
  children,
  client,
  initCheckout,
}) => {
  const [checkoutId, setCheckoutId] = useState<string>("");
  const [paymentId, setPaymentId] = useState<string>("");
  const { checkout, checkoutRef, setCheckout } = useCheckout();
  const [promocodeError, setPromocodeError] = useState("");
  const [bonuses, setBonuses] = useState(0);
  useEffect(() => {
    async function getCheckout() {
      const { checkoutId, checkout } = await initCheckout(client);
      setCheckoutId(checkoutId);
      setCheckout(checkout);
    }
    getCheckout();
  }, []);
  const {
    addToCart,
    removeFromCart,
    ingredientIds,
    optimisticLineItems,
    updateOptimisticLineItems,
  } = useLineItems({ checkoutId, paymentId, client, setCheckout, checkoutRef });
  const { makeOrder } = useOrder({
    checkoutId,
    client,
  });
  async function updateCheckoutAndSave(data: CheckoutUpdateInput) {
    try {
      const newCheckout = await updateCheckout(
        client,
        checkoutId || checkoutRef.current.id,
        paymentId,
        data,
        (err) => setPromocodeError(err)
      );

      let newCheckoutWithPayment;
      if (!newCheckout) {
        if (paymentId) {
          newCheckoutWithPayment = await getCheckoutWithPayment(paymentId);
        }
        throw new Error("Update checkout return nothing!");
      }
      if (paymentId) {
        newCheckoutWithPayment = await getCheckoutWithPayment(paymentId);
      }
      setCheckout(newCheckoutWithPayment || newCheckout);
      updateOptimisticLineItems();
      return newCheckoutWithPayment || newCheckout;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      return false;
    }
  }

  async function getCheckoutWithPayment(paymentId: string) {
    setPaymentId(paymentId);
    try {
      const newCheckout = await getCheckoutInfoWithPaymentId(
        client,
        checkoutId || checkoutRef.current.id,
        paymentId
      );
      if (!newCheckout) {
        throw new Error("Get checkout return nothing!");
      }
      setCheckout(newCheckout);
      updateOptimisticLineItems();
      return newCheckout;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      return checkoutRef.current;
    }
  }
  return (
    <CheckoutContext.Provider
      value={{
        checkout,
        checkoutRef,
        addToCart,
        removeFromCart,
        optimisticLineItems,
        ingredientIds,
        makeOrder,
        updateCheckoutAndSave,
        getCheckoutWithPayment,
        promocodeError,
        bonuses,
        setBonuses,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};
